import { z } from "zod";

import { httpUrl, optionalEnum } from "./_helpers/optional";
import {
  CommunicationInsertSchema,
  CommunicationSchema,
  communicationLifecycleStages,
  communicationSources,
} from "./communication.schema";

// ADV-714: relational attachment store for core.communications.
// One row per file attached to a communication; aligned with
// supabase/migrations/20260503204823_adv_714_communication_attachments.sql.

export const CommunicationAttachmentSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().describe("Account"),
  communication_id: z.string().uuid().describe("Communication"),
  ordinal: z.number().int().nonnegative().describe("Ordinal"),
  url: z.string().describe("URL"),
  mime_type: z.string().nullable().optional().describe("MIME Type"),
  caption: z.string().nullable().optional().describe("Caption"),
  taken_at: z.string().nullable().optional().describe("Taken At"),
  width_px: z.number().int().nullable().optional().describe("Width Pixels"),
  height_px: z.number().int().nullable().optional().describe("Height Pixels"),
  size_bytes: z.number().int().nullable().optional().describe("Size Bytes"),
  metadata: z.record(z.unknown()).nullable().optional().describe("Metadata"),
  media_item_id: z.string().uuid().nullable().optional().describe("Media Item"),
  created_at: z.string().describe("Created At"),
});
export type CommunicationAttachment = z.infer<
  typeof CommunicationAttachmentSchema
>;

// Insert payload — server-generated and parent-derived fields stripped.
// communication_id, business_id, account_id are injected by the service.
// ADV-1111: media_item_id is the optional FK to the canonical ops.media_items
// catalog row — populated by the import handler after the per-attachment
// fetch/EXIF/R2 pipeline runs; left null/undefined by manual or web callers
// that only have metadata.
export const CommunicationAttachmentInsertSchema = z.object({
  ordinal: z.number().int().nonnegative().describe("Ordinal"),
  // ADV-1254: tenant-supplied URL is rendered as <a href> / <img src> in the
  // activity feed. Restrict to absolute http(s) so a `javascript:`/`data:`
  // attachment URL cannot execute script in the viewer's session.
  url: httpUrl().describe("URL"),
  mime_type: z.string().optional().describe("MIME Type"),
  caption: z.string().optional().describe("Caption"),
  taken_at: z.string().optional().describe("Taken At"),
  width_px: z.number().int().optional().describe("Width Pixels"),
  height_px: z.number().int().optional().describe("Height Pixels"),
  size_bytes: z.number().int().optional().describe("Size Bytes"),
  metadata: z.record(z.unknown()).optional().describe("Metadata"),
  media_item_id: z.string().uuid().nullable().optional().describe("Media Item"),
});
export type CommunicationAttachmentInsert = z.input<
  typeof CommunicationAttachmentInsertSchema
>;

// Combined create payload: a single API call inserts the parent communication
// and 0..N attachments. lifecycle_stage and source are re-declared here so
// callers can spread the payload directly.
export const CreateCommunicationWithAttachmentsSchema =
  CommunicationInsertSchema.extend({
    lifecycle_stage: optionalEnum(communicationLifecycleStages).describe(
      "Lifecycle Stage",
    ),
    source: optionalEnum(communicationSources).describe("Source"),
    attachments: z
      .array(CommunicationAttachmentInsertSchema)
      .optional()
      .describe("Attachments"),
  });
export type CreateCommunicationWithAttachments = z.input<
  typeof CreateCommunicationWithAttachmentsSchema
>;

// Response shape for endpoints that return parent + children together.
export const CommunicationWithAttachmentsSchema = z.object({
  communication: CommunicationSchema.describe("Communication"),
  attachments: z.array(CommunicationAttachmentSchema).describe("Attachments"),
});
export type CommunicationWithAttachments = z.infer<
  typeof CommunicationWithAttachmentsSchema
>;
